import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="max-w-[1400px] mx-auto px-20 h-28 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/a2144585b096b0c5bc2cbabd4c2b6ea8/e662b63e618aedfff5c098ee5ba35e0a.png" 
                alt="DataVerge Solutions Logo" 
                className="h-32 w-auto"
              />
            </Link>
          </div>
          
          <div className="flex items-center gap-10">
            <Link to="/#solutions" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-base whitespace-nowrap">Solutions</Link>
            <Link to="/#insights" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-base whitespace-nowrap">Insights</Link>
            <Link to="/#about" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-base whitespace-nowrap">About</Link>
            <Link to="/#contact" className="text-[#000080] hover:text-[#FF6B35] transition-colors cursor-pointer text-base whitespace-nowrap">Contact</Link>
          </div>

          <Link to="/#contact" className="bg-[#FF6B35] text-white px-8 py-3 rounded-lg hover:bg-[#000080] transition-all flex items-center gap-2 whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer">
            Get Started
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-[1000px] mx-auto px-20 py-20">
        <h1 className="text-6xl font-bold text-[#000080] mb-8">Privacy Policy</h1>
        <div className="text-sm text-[#708090] mb-16">Last Updated: January 22, 2026</div>

        <div className="space-y-12 text-[#708090] leading-relaxed">
          <section>
            <h2 className="text-3xl font-bold text-[#000080] mb-6">1. Introduction</h2>
            <p className="text-lg mb-4">
              DataVerge Solutions LLC ("we," "our," or "us") respects your privacy and is committed to protecting the confidential information of our clients and research partners. This Privacy Policy explains how we collect, use, and safeguard information when you visit our website (datavergesolutions.com) or participate in our operational research and benchmarking initiatives.
            </p>
            <p className="text-lg">
              By accessing our services or providing research data, you agree to the terms of this policy.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-[#000080] mb-6">2. Information We Collect</h2>
            <p className="text-lg mb-6">
              We collect information strictly to validate operational inefficiencies and improve business performance for home care agencies.
            </p>

            <h3 className="text-xl font-bold text-[#000080] mb-4">A. Information You Provide Voluntarily</h3>
            <ul className="list-disc pl-8 space-y-2 text-lg mb-6">
              <li><strong className="text-[#FF6B35]">Contact Information:</strong> Name, email address, phone number, and agency name when you fill out contact forms or reply to our outreach.</li>
              <li><strong className="text-[#FF6B35]">Operational Benchmarks:</strong> Data regarding manual audit hours, claim denial rates, revenue leakage estimates, and software usage (e.g., Sandata, NCTracks, Optum) provided during research or consultation.</li>
              <li><strong className="text-[#FF6B35]">Business Inquiries:</strong> Information submitted regarding your specific operational challenges or "High Risk" compliance flags.</li>
            </ul>

            <h3 className="text-xl font-bold text-[#000080] mb-4">B. Automatically Collected Information</h3>
            <ul className="list-disc pl-8 space-y-2 text-lg">
              <li><strong className="text-[#FF6B35]">Usage Data:</strong> We may collect non personal information such as your IP address, browser type, and page visit history to ensure the security and performance of our website.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-[#000080] mb-6">3. How We Use Your Information</h2>
            <p className="text-lg mb-4">
              We do not sell, rent, or trade your personal or business data to third parties. We use your information strictly for the following purposes:
            </p>
            <ul className="list-disc pl-8 space-y-2 text-lg">
              <li><strong className="text-[#FF6B35]">Research &amp; Benchmarking:</strong> To aggregate anonymous data on industry trends (e.g., "Average hours spent on EVV audits in NC"). Individual agency data is never published without explicit consent.</li>
              <li><strong className="text-[#FF6B35]">Communication:</strong> To reply to your inquiries, provide the benchmarks you requested, or schedule consultations.</li>
              <li><strong className="text-[#FF6B35]">Service Improvement:</strong> To validate the development of internal tools and solutions designed to solve the operational pain points you have identified.</li>
              <li><strong className="text-[#FF6B35]">Legal Compliance:</strong> To comply with applicable laws and regulations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-[#000080] mb-6">4. Data Protection &amp; Confidentiality</h2>
            <p className="text-lg mb-6">
              Given our focus on the healthcare sector, we treat all data with a high standard of confidentiality.
            </p>
            <ul className="list-disc pl-8 space-y-2 text-lg">
              <li><strong className="text-[#FF6B35]">No Protected Health Information (PHI):</strong> Our website and research initiatives are not intended to collect PHI (patient names, medical records, etc.). Please do not submit patient level data through our contact forms or email.</li>
              <li><strong className="text-[#FF6B35]">Security Measures:</strong> We implement reasonable security practices (including secure servers and limited access protocols) to protect your business information from unauthorized access.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-[#000080] mb-6">5. Third-Party Services</h2>
            <p className="text-lg">
              We may use trusted third-party service providers (such as Google Analytics or secure email servers) to facilitate our operations. These providers are bound by confidentiality agreements and are prohibited from using your data for any other purpose.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-[#000080] mb-6">6. Your Rights</h2>
            <p className="text-lg mb-4">You have the right to:</p>
            <ul className="list-disc pl-8 space-y-2 text-lg mb-6">
              <li>Request a copy of the information we hold about your agency.</li>
              <li>Request the deletion of your contact information from our database.</li>
              <li>Opt-out of future communications or research studies at any time.</li>
            </ul>
            <p className="text-lg">
              To exercise these rights, please contact us at Info@datavergesolutions.com.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-[#000080] mb-6">7. Changes to This Policy</h2>
            <p className="text-lg">
              We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. The "Last Updated" date at the top of this page will indicate the latest revision.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-[#000080] mb-6">8. Contact Us</h2>
            <p className="text-lg mb-4">
              If you have questions about this Privacy Policy or our data handling practices, please contact us:
            </p>
            <div className="bg-[#F5F6F7] rounded-xl p-8 space-y-3">
              <p className="text-lg"><strong className="text-[#000080]">DataVerge Solutions LLC</strong></p>
              <p className="text-lg">Raleigh, North Carolina</p>
              <p className="text-lg">Email: Info@datavergesolutions.com</p>
              <p className="text-lg">Phone: 919-275-0574</p>
            </div>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-gray-200">
          <Link to="/" className="text-[#FF6B35] hover:text-[#000080] transition-colors flex items-center gap-2 text-lg font-medium cursor-pointer">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#708090] py-16 mt-20">
        <div className="max-w-[1400px] mx-auto px-20">
          <div className="border-t border-white/20 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-white/80">
                © 2025 DataVerge Solutions LLC. All rights reserved.
              </div>
              <div className="flex items-center gap-8">
                <Link to="/privacy-policy" className="text-sm text-white hover:text-white/80 transition-colors cursor-pointer">Privacy Policy</Link>
                <Link to="/terms" className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Terms</Link>
                <Link to="/accessibility" className="text-sm text-white/80 hover:text-white transition-colors cursor-pointer">Accessibility</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
